import { detectOs, getUserTokenKey } from 'app/utils/browser'
import { LOGO_URL, NEW_DEFAULT_LOGOS } from 'app/utils/urls'
import { getWidgetFeatureAccessObject } from 'app/redux/messages/messages.adapter'
import { getHostWebsiteUrl, getNormalizedUrl, matchUrlPattern } from 'app/utils/links'
import { DEFAULT_AVATAR_NAME } from 'app/constants/avatar'
import { shuffleArray } from 'app/utils/array'

function getSplittedPositionArray(position = 'bottom-right') {
  const separatedByDash = position.split('-')
  const separatedBySpace = position.split(' ')

  if (separatedByDash.length === 2) return separatedByDash
  if (separatedBySpace.length === 2) return separatedBySpace
  return ['bottom', 'right']
}

function getOffsetNumber(offset = '') {
  if (!offset) return '0'
  const value = offset.trim()
  if (value.slice(-2) === 'px') return value.slice(0, value.length - 2)
  return value
}

const getPositions = (position = {}, directions = 'bottom-right') => {
  const splittedDirection = getSplittedPositionArray(directions)

  const coordinates = {
    top: 'auto',
    right: '15px',
    bottom: '15px',
    left: 'auto',
  }

  Object.keys(coordinates).forEach((key) => {
    if (position[key] && splittedDirection.includes(key)) {
      delete coordinates[key]
      coordinates[`${key}Space`] = getOffsetNumber(position[key]) + 'px'
      return
    }
    if (splittedDirection.includes(key)) {
      coordinates[`${key}Space`] = '15px' // default coordinate
      return
    }
    delete coordinates[key]
    coordinates[`${key}Space`] = 'auto'
  })

  return coordinates
}

const nullChecker = (value) => {
  if (value && value !== 'null') return value
  return ''
}

const getConditionTime = (condition) => {
  return condition.value
}

/**
 *
 * @param {Operator[]} operators
 */
const getFullSenderInfo = (sender, operators) => {
  let senderOperator

  if (!sender) {
    senderOperator = operators[Math.floor(Math.random() * operators.length)]
  } else {
    senderOperator = operators.find((op) => op.id === sender.id) || operators[0]
  }

  return {
    id: senderOperator.id,
    avatar: senderOperator.avatar,
    title: senderOperator.name,
  }
}

const checkIfConditionIsInPublishRange = (condition, currentDate) => {
  const from = new Date(condition.publish.starter).getTime()
  const end = condition.publish.end === 'never' ? Infinity : new Date(condition.publish.end)
  const now = new Date(currentDate).getTime()

  return now > from && now < end
}

const checkIfWhenConditionIsSatisfied = (condition, isOfficeHour) => {
  const when = condition.publish.when

  return when === 'anyTime' || (when === 'officeHours' && isOfficeHour) || (when === 'nonOfficeHours' && !isOfficeHour)
}

const checkIfPublishConditionIsSatisfied = (condition, isOfficeHour, currentDate) => {
  const isWhenConditionSatisfied = condition.publish && checkIfWhenConditionIsSatisfied(condition, isOfficeHour)

  const isInPublishRange = condition.publish && checkIfConditionIsInPublishRange(condition, currentDate)

  return isWhenConditionSatisfied && isInPublishRange
}

/**
 * form object that is used in linger
 * @typedef Form
 * @type {object}
 * @property {string} label - label for form field
 * @property {string} placeholder - placeholder for the field
 * @property {string('number' | 'phone' | 'string' | 'email' | 'name')} type - input type in form
 */

/**
 * linger (response delay) object
 * @typedef Linger
 * @type {object}
 * @property {number} index - index of the linger
 * @property {string('minute' | 'second')} conditionType - type of time condition
 * @property {number} condition - value of time condition
 * @property {boolean} isActive - linger status (active or inactive)
 * @property {boolean} isAllTeam - is this linger applied to all teams
 * @property {string} message - the linger message
 * @property {boolean} sound - does linger arrival has notification sound
 * @property {number[]} teamsIds - array of department ids that linger should get applied to
 * @property {number(1 | 2)} type - type of conversation that linger should apply to (pending (1) or in-conversation (2))
 * @property {Form} forms - the form that should be shown (it is optional)
 * @property {boolean} active - whether linger is active or not
 */

/**
 * Returns an array of lingers (response delay) object
 * @param {Linger[]} lingers - The linger setting objects.
 **/
const getAdaptedLingersArray = (lingers) => lingers?.filter((item) => item.isActive) || []

/**
 * @typedef {{
 *  operators: Operator[]
 * }} inputParamObject
 *
 * @param {inputParamObject}
 */
const getWidgetTrigger = ({ triggerItems, operators, isOfficeHour, currentDate, hideInNoneOfficeHours }) => {
  if (!isOfficeHour && hideInNoneOfficeHours) {
    return {
      messages: [],
      times: {},
      maxTime: null,
    }
  }

  let adaptedTrigger = []
  let triggerTimes = {}
  let maxTime = 0
  let conditionIndex = 0

  let rayToken = localStorage.getItem(getUserTokenKey())

  triggerItems.forEach((item) => {
    // check publish section conditions
    const isPublishConditionIsSatisfied = checkIfPublishConditionIsSatisfied(item, isOfficeHour, currentDate)

    if (!isPublishConditionIsSatisfied) return

    let showOnce = item.justOnce
    const showInMobile = item.mobile
    let { mobile } = detectOs()

    const isMobileConditionChecked = (mobile && showInMobile) || !mobile
    const isShowOnceConditionChecked = !showOnce || (showOnce && !rayToken)

    if (item.enabled && isShowOnceConditionChecked && isMobileConditionChecked) {
      adaptedTrigger.push({
        ...item,
        id: item._id,
        sender: getFullSenderInfo(item.sender, operators),
      })
      item.conditions &&
        item.conditions.forEach((condition) => {
          if (condition.cond === '1') {
            triggerTimes[conditionIndex] = getConditionTime(condition)
            conditionIndex++
          }
        })
    }
  })

  for (let key in triggerTimes) {
    if (Number(triggerTimes[key]) > maxTime) {
      maxTime = Number(triggerTimes[key])
    }
  }

  return {
    messages: adaptedTrigger,
    times: triggerTimes,
    maxTime: maxTime,
  }
}

const getAllowCustomLogo = ({ isCustomLogo, packageType, logo }) => {
  const packagesCanSetCustomLogo = ['premiumPlus', 'premium', 'trial', 'vip']

  if (packagesCanSetCustomLogo.includes(packageType) && isCustomLogo && (logo == '7' || !logo)) {
    return true
  }

  return false
}

const getCoverLogo = (logo) => {
  if (logo) {
    //for cover
    // return `${LOGO_URL}/${personalize.logo}${personalize.logo}.svg`;
    //for button
    return `${NEW_DEFAULT_LOGOS}/${logo}.svg`
  } else {
    return `${NEW_DEFAULT_LOGOS}/1.svg`
  }
}

export const getLuancherIcon = ({ packageType, logo, isCustomLogo }) => {
  const packagesSupport = ['growth', 'premium', 'trial', 'premiumPlus', 'vip']
  const packagesNotSupport = ['professional', 'free', 'expire']

  if (packagesNotSupport.includes(packageType)) {
    return getCoverLogo(logo)
  } else if (packagesSupport.includes(packageType)) {
    if (isCustomLogo && (logo == '7' || !logo)) {
      return getNormalizedUrl(isCustomLogo)
    } else {
      return getCoverLogo(logo)
    }
  }
}

const getShowWidgetStatus = (personalizeData) => {
  let { mobile } = detectOs()
  if (mobile && !personalizeData.loadInPhone) {
    return false
  } else {
    return true
  }
}

const getTimeDetailes = (time) => {
  let adaptedTime = {}
  let timeArray = time.split('')
  adaptedTime.hour = Number(`${timeArray[0]}${timeArray[1]}`)
  adaptedTime.minute = Number(`${timeArray[timeArray.length - 2]}${timeArray[timeArray.length - 1]}`)

  return adaptedTime
}

const getCurrentDate = (date) => {
  try {
    return new Date(date)
  } catch (error) {
    console.log(error)
  }
}

let minutesOfDay = function (m) {
  return Number(m.minute) + Number(m.hour) * 60
}

const checkIfOfficeHour = (officeHours, date) => {
  let currentDate = getCurrentDate(date)
  let weekday = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday']
  let isOfficeHour = false
  let currentWeekDay = weekday[currentDate.getDay()]
  let isWorkDay = currentWeekDay === 'Friday' ? false : true

  if (officeHours) {
    officeHours.forEach((item) => {
      if (item.key === currentWeekDay || (item.key === 'workDays' && isWorkDay) || item.key === 'entireWeek') {
        let currentTimeMinutes = minutesOfDay({
          hour: currentDate.getHours(),
          minute: currentDate.getMinutes(),
        })

        let startTimeMinutes = minutesOfDay(getTimeDetailes(item.from))
        let endTimeMinutes = minutesOfDay(getTimeDetailes(item.to))

        const startTimeBiggerThanEnd = startTimeMinutes > endTimeMinutes

        // checks office hour when start time is bigger than end time
        const isOfficeHourWhenStartIsBiggerThanEnd =
          startTimeBiggerThanEnd && !(endTimeMinutes <= currentTimeMinutes && currentTimeMinutes <= startTimeMinutes)

        // checks office hour when start time is smaller than end time
        const isOfficeHourWhenStartIsSmallerThanEnd =
          !startTimeBiggerThanEnd && currentTimeMinutes >= startTimeMinutes && currentTimeMinutes <= endTimeMinutes

        if (isOfficeHourWhenStartIsBiggerThanEnd || isOfficeHourWhenStartIsSmallerThanEnd) {
          isOfficeHour = true
        }
      }
    })
  } else {
    isOfficeHour = true
  }

  if (officeHours && officeHours.length === 0) {
    isOfficeHour = true
  }

  return isOfficeHour
}

/**
 * This function returns proper adapted teams(departments).
 * Initially it checks if there is any operator for link assignemt.
 * if there is then it returns only one team with assigned operator otherwise it returns teams.
 *
 * @param {Object} apiResponse
 * @returns {Array}
 */
const getTeams = ({ allTeams, urlAssignedOps }) => {
  if (urlAssignedOps) {
    const defaultTeam = allTeams.find((item) => item.default === 'true') || allTeams[0]

    return [
      {
        ...defaultTeam,
        operators: urlAssignedOps,
      },
    ]
  }

  return allTeams
}

/**
 * This function returns operators assigned to the current (websiteHost) url
 *
 * @param {*} urlAssignments
 * @param {Operator[]} allOperators\
 *
 * @returns {Array<Operator> | undefined}
 */
const getOperatorsAssignedToCurrentUrl = (urlAssignments, allOperators) => {
  const currentUrl = getHostWebsiteUrl()
  const urlAssignedOpsIds = urlAssignments?.find((item) => matchUrlPattern(currentUrl, item.url))?.agentsId

  if (!urlAssignedOpsIds) return

  const operators = allOperators.filter((op) => urlAssignedOpsIds.includes(op.id))

  return operators
}

/**
 * Get array of adapted teams(departments) with related operators
 *
 * @typedef {{
 *  agents: string[]
 *  color: string,
 *  default: string,
 *  isUserUpdated: boolean,
 *  logo: string,
 *  name: string,
 *  _id: string
 * }} teamFromApi
 *
 * @returns {{
 * ...teamFromApi,
 * operators: Operator[]
 * }[]} adapted teams
 */
const getAdaptedTeams = (teams, operators) =>
  teams.map((t) => {
    const teamOps = operators.filter((op) => t.agents.includes(op.id))
    const isLogoDefault = t.logo.includes(DEFAULT_AVATAR_NAME)

    return {
      ...t,
      isLogoDefault,
      operators: shuffleArray(teamOps),
    }
  })

const getLanguageObject = (apiResponse) => {
  const { title, title2 } = apiResponse.personalize
  const language = apiResponse.landing.lang
  const { direction, align, opposeDirection, opposeAlign, ...rest } = apiResponse.lang || {}

  const general = {
    title,
    title2,
    direction,
    align,
    opposeDirection,
    opposeAlign,
    language: language || 'fa',
  }

  const languageObj = {
    general,
    texts: { ...rest, title, title2 },
  }

  return languageObj
}

/**
 *
 * @param {InputOperator[]} operators
 * @returns {Operator[]}
 */
const getAdaptedOperators = (operators) =>
  operators.map((op) => ({
    id: op.id,
    avatar: op.meta.avatar,
    online: op.meta.online,
    title: op.meta.title,
    name: op.name,
  }))

/**
 * response bot's main option object
 * @typedef {object} option
 * @property {string} id - id of the bot
 * @property {boolean} [isActive] - whether this bot is enabled or not (appears only in first layer of response)
 * @property {string} [teamId] - teamId to specify which team this bot in enabled for (appears only in first layer of response). if none is specified then it means bot belongs to all departments
 * @property {string} question - question
 * @property {string} response - can be markup
 * @property {object[]} [options] - an array of objects made of options (optional)
 * @property {string("text" | "event" | "redirect" | "question" | null)} responseType - type of response, it is null in the first layer
 */

/**
 * @return {option[]} array of enabled answering bot options
 */
const getResponseBotData = (apiResponse) => {
  const bots = apiResponse || []

  return bots.filter((item) => item.isActive).map((item) => ({ ...item }))
}

export const createWidgetInfoObject = (apiResponse) => {
  const operators = shuffleArray(getAdaptedOperators(apiResponse.operators))
  const urlAssignedOps = getOperatorsAssignedToCurrentUrl(apiResponse.assignedUrls, operators)
  const featureAccesses = getWidgetFeatureAccessObject(apiResponse.channel.package)
  const adaptedTeams = getAdaptedTeams(apiResponse.team, operators)
  const packageType = apiResponse.channel.package.type

  /**
   * NOTE: the reason I refused to read office hours from server use this was that in one senario it can cause corner
   * case: when visitor opens site before office hour but sends message after office hour
   * so it is best to calculate `isOfficeHour` freshly in front side
   *
   **/
  let isOfficeHour = checkIfOfficeHour(
    Object.keys(apiResponse.officeHours).length === 0 ? [] : apiResponse.officeHours,
    apiResponse.date,
  )

  const hideInNoneOfficeHours = apiResponse.notOfficeHours?.disableTriggers === 'true'
  const notOfficeHoursAndShowSleepLogoEnabled = !isOfficeHour && apiResponse.notOfficeHours?.sleepLogo == 'true'
  const sleepLogoUrl = `${LOGO_URL}/raychat-logo-sleep.svg`
  const isCustomLogo = featureAccesses.customLogo ? nullChecker(apiResponse.personalize.isCustomLogo) : '' // uploading logo
  const logo = nullChecker(apiResponse.personalize.logo) // default logo

  // TODO: remove this implicit mapping and explicitly express all needed fields
  let adaptedObject = {
    ...apiResponse,
    personalize: {
      wallpaper: apiResponse.personalize.wallpaper,
      seColor: apiResponse.personalize.seColor,
      forms: featureAccesses.sendForm ? apiResponse.forms : {},
      bgc: apiResponse.personalize.bgColor,
      title: apiResponse.landing.title || '',
      title2: apiResponse.landing?.desc || '',
      startMessagingTitle: apiResponse.landing?.sendForMe || '',
      startMessagingDesc: apiResponse.landing?.backMessage || '',
      isCustomLogo,
      logo,
      loadInPhone: !!apiResponse.position?.loadInPhone,
      backgroundLogo: nullChecker(apiResponse.personalize.backgroundLogo),
      position: getPositions(apiResponse.position || {}, apiResponse.position?.position),
      headerTitleColor: apiResponse.personalize.headerTitleColor,
    },
    channel: {
      id: apiResponse.channel.id,
      containerId: apiResponse.channel.containerId,
      token: apiResponse.channel.token,
      package: {
        type: packageType,
        index: apiResponse.channel.package.index,
        disableRayChatLogo: apiResponse.channel.package.disableRayChatLogo,
        botResponser: apiResponse.channel.package.botResponser,
        customeLogo: apiResponse.channel.package.customeLogo,
        trigger: apiResponse.channel.package.trigger,
        waitingForm: apiResponse.channel.package.waitingForm,
        realTimeUserTypedMessage: apiResponse.channel.package.realTimeUserTypedMessage,
        sendVoiceWidget: apiResponse.channel.package.sendVoiceWidget,
        mediaUploadLimit: {
          widget: apiResponse.channel.package.mediaUploadLimit.widget,
        },
      },
    },
    operators: urlAssignedOps || operators,
    teams: getTeams({
      allTeams: adaptedTeams,
      urlAssignedOps,
    }),
    lingers: featureAccesses.lingers ? getAdaptedLingersArray(apiResponse.lingers) : [],
    responseBotData: featureAccesses.responseBot ? getResponseBotData(apiResponse.bots) : [],
    uploadFileEnabled: apiResponse.uploader,
    featureAccesses,
    // these checking are because of inconsistency in data retrieved from backend
    aiBotEnabled: typeof apiResponse.isBot === 'object' ? apiResponse.isBot.isEnabled : apiResponse.isBot,
    isConnectWithOperatorEnabled: typeof apiResponse.isBot === 'object' ? apiResponse.isBot.connectBTN : true,
    publicSetting: {
      sendFileEnabled: featureAccesses.uploadFileLimit && (apiResponse.publicSetting.sendFileWidget ?? true),
      sendVoiceWidgetEnabled: featureAccesses.sendVoiceMsg && apiResponse.publicSetting.sendVoiceWidget,
      hideRaychatLogoBar: featureAccesses.disableRayChatLogo && apiResponse.publicSetting.hideRaychatLogo,
      operatorCanSeeUserTypingWordsEnabled:
        featureAccesses.seeTypingWords && apiResponse.publicSetting.SeeTypedSentences,
    },
    allowCustomLogo: !notOfficeHoursAndShowSleepLogoEnabled
      ? getAllowCustomLogo({ packageType, isCustomLogo, logo })
      : false,
    coverIcon: notOfficeHoursAndShowSleepLogoEnabled
      ? sleepLogoUrl
      : getLuancherIcon({ packageType, logo, isCustomLogo }),
    trigger: getWidgetTrigger({
      triggerItems: apiResponse.triggers,
      operators,
      isOfficeHour,
      currentDate: apiResponse.date,
      hideInNoneOfficeHours,
    }),
  }

  return {
    languageInfo: getLanguageObject(apiResponse),
    data: adaptedObject,
    isOfficeHour,
    // filter both active departments and departments that have at least one question
    faq: apiResponse.faq.filter((f) => !f.disabled && f.questions.length),
    showWidget: getShowWidgetStatus(adaptedObject.personalize),
  }
}
